import { create } from 'zustand';
import type { BoardState } from '@draughts/shared';

/**
 * Il passaggio di mano fra la revisione partita e l'editor d'analisi.
 *
 * ⚠️ **Questo store esiste per cio' che NON importa.** Oltre a `zustand` non ha
 * un solo import a runtime, e deve restare cosi'.
 *
 * La review ([GameReplay]) ha bisogno di sapere due cose che non puo' leggere da
 * se':
 *
 *  - «c'e' una partita in corso?», che vive in `multiplayer-store`,
 *    `arena-store`, `tournament-store` e `game-store` — i quali tirano dentro
 *    `services/socket`, `services/api` e `@draughts/core`. Importarli dalla
 *    review significherebbe ficcare il motore e la socket nel chunk della
 *    revisione, e far esplodere i suoi test, che mockano `@draughts/core` con
 *    una *factory* che espone solo `createInitialBoard`/`formatMoveNotation`;
 *  - «come ci si va?», cioe' `setScreen`, che e' uno `useState` locale di App.
 *
 * Quindi: **App pubblica il fatto (`gameInProgress`), la review deposita la
 * richiesta (`pending`), App la raccoglie e naviga.** Nessuna prop da passare a
 * mano lungo cinque punti di montaggio — ed e' esattamente il difetto che questo
 * store ripara: dei cinque ospiti della review la prop la riceveva in due, e
 * «Studia in editor» non compariva nei tornei, nelle arene e nel profilo aperto
 * in overlay.
 *
 * ⚠️ Viaggia un `BoardState` e **non** una FEN. Costruirne una qui vorrebbe dire
 * un `boardStateToFen` da `@draughts/core`, cioe' di nuovo l'import che tutto
 * questo giro serve a evitare. Chi la riceve (`editor-store.loadPosition`) la
 * ribasa comunque su `createInitialBoard`.
 */
interface StudyStore {
  /**
   * La schermata sotto ha una partita viva. **Lo scrive SOLO App**, che e' l'unico
   * posto da cui si vedono i quattro store di partita; qui ci arriva attraverso
   * `useStudyHandoff`.
   *
   * Parte da `false` di proposito: finche' App non ha pubblicato niente il
   * modo di sbagliare e' «l'azione e' disponibile», non «l'azione e' sparita».
   */
  gameInProgress: boolean;
  /** La posizione che aspetta di aprirsi nell'editor, o `null`. */
  pending: BoardState | null;
  setGameInProgress: (inProgress: boolean) => void;
  requestStudy: (position: BoardState) => void;
  /**
   * Legge **e svuota**: una richiesta vale una volta sola.
   *
   * E' cio' che rende innocuo il doppio montaggio di StrictMode e, piu' in
   * generale, un effetto che gira due volte sullo stesso stato: il secondo giro
   * trova `null` e non riapre l'editor.
   */
  takeStudyRequest: () => BoardState | null;
}

export const useStudyStore = create<StudyStore>((set, get) => ({
  gameInProgress: false,
  pending: null,
  setGameInProgress: (gameInProgress) => set({ gameInProgress }),
  requestStudy: (position) => set({ pending: position }),
  takeStudyRequest: () => {
    const { pending } = get();
    if (pending) set({ pending: null });
    return pending;
  },
}));
